import {
    Clock,
    MapPin,
    Plus,
    Search,
    User,
    X
} from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../../hooks/useToast';
import { TrackingTimeline } from '../TrackingTimeline';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Input } from '../../ui/input';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Textarea } from '../../ui/textarea';
import {
  buildRecentAwbList,
  clearRecentAwbs,
  loadRecentAwbs,
  normalizeAwb,
  saveRecentAwbs,
} from '../../../utils/recentAwbs';

interface TrackingEvent {
  _id?: string;
  eventDate: Date;
  eventTime: string;
  eventDescription: string;
  eventLocation: string;
  eventType: 'MANUAL' | 'API';
  eventState: string;
  remarks?: string;
  createdDate: Date;
  createdBy?: {
    _id: string;
    name: string;
    email: string;
  };
}

interface ShipmentData {
  awbNumber: string;
  currentStatus: string;
  customer: {
    name: string;
  };
  origin: {
    name: string;
  };
  destination: {
    name: string;
  };
  trackingEvents: TrackingEvent[];
}

interface ShipmentControlProps {
  initialAwb?: string;
}

const RECENT_AWBS_KEY = 'trackingControlRecentAwbs';
const MAX_RECENT_AWBS = 6;

export const ShipmentControl: React.FC<ShipmentControlProps> = ({ initialAwb }) => {
  const [searchQuery, setSearchQuery] = useState(initialAwb || '');
  const [shipment, setShipment] = useState<ShipmentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const [eventStatusFilter, setEventStatusFilter] = useState('all');
  const [recentAwbs, setRecentAwbs] = useState<string[]>(() =>
    loadRecentAwbs(RECENT_AWBS_KEY, MAX_RECENT_AWBS)
  );
  const searchIdRef = useRef(0);
  const { toast } = useToast();
  const { axios } = useAuth();

  const [formData, setFormData] = useState({
    eventDate: new Date().toISOString().split('T')[0],
    eventTime: new Date().toTimeString().slice(0, 5),
    eventDescription: '',
    eventLocation: '',
    eventType: 'MANUAL',
    eventState: '',
    remarks: ''
  });

  const eventDescriptions = [
    'Package picked up from sender',
    'Package arrived at origin hub',
    'Package in transit',
    'Package arrived at destination hub',
    'Package out for delivery',
    'Package delivered successfully',
    'Delivery attempted - recipient not available',
    'Package returned to sender',
    'Package held at customs',
    'Package damaged during transit',
    'Address correction required',
    'Package cancelled by sender'
  ];

  const eventStates = [
    'DRAFT',
    'BOOKED',
    'PICKED_UP',
    'IN_TRANSIT',
    'OUT_FOR_DELIVERY',
    'DELIVERED',
    'RETURNED',
    'CANCELLED'
  ];

  // Auto-search if initialAwb is provided
  React.useEffect(() => {
    if (!initialAwb) return;
    const normalizedAwb = normalizeAwb(initialAwb);
    setSearchQuery(normalizedAwb);
    handleSearch(normalizedAwb);
  }, [initialAwb]);

  const handleSearch = async (awbToSearch?: string) => {
    const query = normalizeAwb(awbToSearch ?? searchQuery);
    if (!query) return;

    const normalizedQuery = query;
    setHasSearched(true);

    if (normalizedQuery !== searchQuery) {
      setSearchQuery(normalizedQuery);
    }

    setLoading(true);
    const searchId = ++searchIdRef.current;
    const isSameAwb = shipment?.awbNumber?.toUpperCase() === normalizedQuery;
    if (!isSameAwb) {
      setShipment(null);
    }

    try {
      const response = await axios.get(`/api/tracking/customer?awbNumber=${encodeURIComponent(normalizedQuery)}`);
      const data = response.data;

      if (searchId !== searchIdRef.current) return;

      if (data.success) {
        setShipment(data.data.awb);
        setRecentAwbs((prev) => {
          const next = buildRecentAwbList(prev, normalizedQuery, MAX_RECENT_AWBS);
          saveRecentAwbs(RECENT_AWBS_KEY, next);
          return next;
        });
      } else {
        toast({
          title: "Not Found",
          description: "Shipment not found",
          variant: "destructive"
        });
      }
    } catch (error) {
      if (searchId !== searchIdRef.current) return;
      console.error('Error searching shipment:', error);
      toast({
        title: "Error",
        description: "Failed to search shipment",
        variant: "destructive"
      });
    } finally {
      if (searchId === searchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const clearSearch = () => {
    searchIdRef.current += 1;
    setSearchQuery('');
    setShipment(null);
    setHasSearched(false);
    setLoading(false);
    setEventSearch('');
    setEventStatusFilter('all');
  };

  const handleRecentSelect = (awb: string) => {
    const normalized = normalizeAwb(awb);
    if (!normalized) return;
    setSearchQuery(normalized);
    handleSearch(normalized);
  };

  const clearRecent = () => {
    clearRecentAwbs(RECENT_AWBS_KEY);
    setRecentAwbs([]);
  };

  const handleAddEvent = async () => {
    // Validate and add new event
    if (!shipment) return;

    if (!formData.eventDescription || !formData.eventLocation || !formData.eventState) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(`/api/awb/${shipment.awbNumber}/tracking`, {
        eventDate: formData.eventDate,
        eventTime: formData.eventTime,
        eventDescription: formData.eventDescription,
        eventLocation: formData.eventLocation,
        eventType: formData.eventType,
        eventState: formData.eventState,
        remarks: formData.remarks
      });

      const data = response.data;

      if (data.success) {
        toast({
          title: "Success",
          description: "Tracking event added successfully"
        });
        setShowAddDialog(false);
        handleSearch(); // Refresh data
        
        // Reset form partially
        setFormData(prev => ({
          ...prev,
          eventDescription: '',
          remarks: ''
        }));
      }
    } catch (error: any) {
      console.error('Error adding tracking event:', error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to add tracking event",
        variant: "destructive"
      });
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'DRAFT': 'bg-slate-500',
      'BOOKED': 'bg-blue-600',
      'PICKED_UP': 'bg-amber-600',
      'IN_TRANSIT': 'bg-indigo-600',
      'OUT_FOR_DELIVERY': 'bg-purple-600',
      'DELIVERED': 'bg-emerald-600',
      'RETURNED': 'bg-red-600',
      'CANCELLED': 'bg-slate-700',
    };
    return statusColors[status] || 'bg-slate-500';
  };

  const getEventTimestamp = (event: TrackingEvent) => {
    const eventDate = new Date(event.eventDate);
    if (!Number.isNaN(eventDate.getTime())) {
      if (event.eventTime) {
        const [hours, minutes] = event.eventTime.split(':').map(Number);
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
          const withTime = new Date(eventDate);
          withTime.setHours(hours, minutes, 0, 0);
          return withTime.getTime();
        }
      }
      return eventDate.getTime();
    }

    const createdDate = new Date(event.createdDate);
    if (!Number.isNaN(createdDate.getTime())) {
      return createdDate.getTime();
    }

    return 0;
  };

  const sortedEvents = React.useMemo(() => {
    if (!shipment?.trackingEvents?.length) return [];
    return [...shipment.trackingEvents]
      .filter((event) => !event.eventDescription?.toLowerCase().includes('awb updated'))
      .sort((a, b) => getEventTimestamp(b) - getEventTimestamp(a));
  }, [shipment?.trackingEvents]);

  const latestEvent = sortedEvents[0];
  const deferredEventSearch = React.useDeferredValue(eventSearch);
  const filteredEvents = React.useMemo(() => {
    if (!sortedEvents.length) return [];

    let filtered = sortedEvents;

    if (eventStatusFilter !== 'all') {
      filtered = filtered.filter((event) => event.eventState === eventStatusFilter);
    }

    const searchValue = deferredEventSearch.trim().toLowerCase();
    if (!searchValue) {
      return filtered;
    }

    return filtered.filter((event) => {
      const candidates = [
        event.eventDescription,
        event.eventLocation,
        event.remarks,
        event.eventState,
        event.createdBy?.name,
        event.createdBy?.email,
      ];

      return candidates.some((value) =>
        String(value ?? '').toLowerCase().includes(searchValue)
      );
    });
  }, [sortedEvents, eventStatusFilter, deferredEventSearch]);
  const hasEventFilters =
    eventStatusFilter !== 'all' || Boolean(deferredEventSearch.trim());

  const clearEventFilters = () => {
    setEventSearch('');
    setEventStatusFilter('all');
  };

  const formatEventDate = (event: TrackingEvent) => {
    const timestamp = getEventTimestamp(event);
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString();
  };

  const formatEventTime = (event: TrackingEvent) => {
    if (event.eventTime) return event.eventTime;
    const timestamp = getEventTimestamp(event);
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatEventDateTime = (event: TrackingEvent) => {
    const timestamp = getEventTimestamp(event);
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <Input
            className="pl-9 pr-10 h-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
            placeholder="Search by AWB Number (e.g., SHIP123456)"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleSearch();
              }
              if (e.key === 'Escape') {
                clearSearch();
              }
            }}
          />
          {searchQuery.trim() && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 h-7 w-7 -translate-y-1/2 p-0 text-slate-500 hover:text-slate-700"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button
          type="button"
          onClick={() => handleSearch()}
          disabled={loading || !searchQuery.trim()}
          className="w-full sm:w-auto"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <LoadingSpinner size="sm" className="border-white/50 border-t-white" />
              Searching...
            </span>
          ) : (
            'Find Shipment'
          )}
        </Button>
      </div>

      {recentAwbs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            <Clock className="h-3.5 w-3.5" />
            Recent
          </div>
          {recentAwbs.map((awb) => (
            <Button
              key={awb}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleRecentSelect(awb)}
              disabled={loading}
              className="h-7 px-2 text-xs"
            >
              {awb}
            </Button>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearRecent}
            disabled={loading}
            className="h-7 px-2 text-xs text-slate-500"
          >
            Clear
          </Button>
        </div>
      )}

      {shipment ? (
        <div className="space-y-6 animate-in fade-in-50 slide-in-from-bottom-2 duration-500">
          {/* Status Header */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                 <h2 className="text-2xl font-bold tracking-tight">{shipment.awbNumber}</h2>
                 <Badge className={`${getStatusColor(shipment.currentStatus)} text-white border-0`}>
                   {shipment.currentStatus.replace(/_/g, ' ')}
                 </Badge>
              </div>
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <span className="font-medium text-slate-900 dark:text-slate-100">{shipment.origin?.name || 'Unknown Origin'}</span>
                <span className="text-slate-300 mx-1">→</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{shipment.destination?.name || 'Unknown Dest.'}</span>
              </div>
            </div>
            
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="shrink-0 bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Event
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Update Tracking Status</DialogTitle>
                  <DialogDescription>
                    Add a new tracking event for AWB {shipment.awbNumber}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date</label>
                      <Input
                        type="date"
                        value={formData.eventDate}
                        onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Time</label>
                      <Input
                        type="time"
                        value={formData.eventTime}
                        onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Status *</label>
                    <Select value={formData.eventState} onValueChange={(value) => setFormData({ ...formData, eventState: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status..." />
                      </SelectTrigger>
                      <SelectContent>
                        {eventStates.map((state) => (
                          <SelectItem key={state} value={state}>{state.replace(/_/g, ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Event Description *</label>
                    <Select value={formData.eventDescription} onValueChange={(value) => setFormData({ ...formData, eventDescription: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select description..." />
                      </SelectTrigger>
                      <SelectContent>
                        {eventDescriptions.map((desc) => (
                          <SelectItem key={desc} value={desc}>{desc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Location *</label>
                    <Input
                      value={formData.eventLocation}
                      onChange={(e) => setFormData({ ...formData, eventLocation: e.target.value })}
                      placeholder="e.g. Mumbai Hub"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Remarks</label>
                    <Textarea
                      placeholder="Optional notes..."
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button onClick={handleAddEvent}>Add Event</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Timeline Table */}
          <Card>
            <CardHeader className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle>Tracking History</CardTitle>
                  <CardDescription>Detailed timeline of events for this shipment</CardDescription>
                </div>
                {sortedEvents.length > 0 && (
                  <div className="text-xs text-muted-foreground space-y-1 text-left sm:text-right">
                    <div>
                      {hasEventFilters
                        ? `${filteredEvents.length} of ${sortedEvents.length} event(s)`
                        : `${sortedEvents.length} event(s)`}
                    </div>
                    {latestEvent && (
                      <div>Last update: {formatEventDateTime(latestEvent)}</div>
                    )}
                  </div>
                )}
              </div>
              {(sortedEvents.length > 0 || hasEventFilters) && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="relative flex-1 sm:max-w-xs">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                      <Input
                        value={eventSearch}
                        onChange={(event) => setEventSearch(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            setEventSearch('');
                          }
                        }}
                        placeholder="Filter events by text"
                        className="pl-9 pr-8 h-9"
                        aria-label="Filter tracking events"
                      />
                      {eventSearch.trim() && (
                        <button
                          type="button"
                          onClick={() => setEventSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          aria-label="Clear event search"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="w-full sm:w-52">
                      <Select
                        value={eventStatusFilter}
                        onValueChange={setEventStatusFilter}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All statuses</SelectItem>
                          {eventStates.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {hasEventFilters && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearEventFilters}
                      className="w-full sm:w-auto"
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-6">Status</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Updated By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No tracking events found
                        </TableCell>
                      </TableRow>
                    ) : filteredEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          <div className="space-y-3">
                            <div>No events match the current filters</div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={clearEventFilters}
                            >
                              Clear filters
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEvents.map((event, index) => (
                        <TableRow
                          key={
                            event._id ??
                            `${event.eventDate}-${event.eventTime}-${event.eventDescription}-${index}`
                          }
                        >
                          <TableCell className="pl-6">
                            <Badge
                              variant="outline"
                              className={`${getStatusColor(event.eventState)} bg-opacity-10 dark:bg-opacity-20 text-slate-700 dark:text-slate-200 border-0`}
                            >
                              {event.eventState.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium break-words">{event.eventDescription}</div>
                            {event.remarks && <div className="text-xs text-slate-500 mt-0.5">{event.remarks}</div>}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-slate-600">
                              <MapPin className="h-3.5 w-3.5" />
                              {event.eventLocation || 'Unknown'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col text-sm">
                              <span className="font-medium">{formatEventDate(event)}</span>
                              <span className="text-slate-500 text-xs">{formatEventTime(event)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-slate-500">
                              <User className="h-3.5 w-3.5" />
                              <span className="text-sm">{(event.createdBy as any)?.name || 'System'}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Timeline */}
              <div className="md:hidden p-4">
                {sortedEvents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No tracking events found
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground space-y-3">
                    <div>No events match the current filters</div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearEventFilters}
                    >
                      Clear filters
                    </Button>
                  </div>
                ) : (
                  <TrackingTimeline events={filteredEvents} />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        !loading && hasSearched && searchQuery.trim() && (
           <div className="text-center py-20 border-2 border-dashed rounded-xl border-slate-200 dark:border-slate-800">
             <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-900 mb-4">
               <Search className="h-6 w-6 text-slate-400" />
             </div>
             <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">No shipment selected</h3>
             <p className="text-slate-500">Enter an AWB number above to view details</p>
           </div>
        )
      )}
    </div>
  );
};
